import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Phone, MapPin, Clock, ArrowRight, ShieldCheck } from "lucide-react";
import {
  RelatedPages,
  allTreatmentsLinks,
  allDecisionLinks,
} from "@/components/RelatedPages";
import { EegTrace } from "./prism/EegTrace";
import PrismVideosSection from "./prism/PrismVideosSection";
import SymptomsSection from "./prism/SymptomsSection";
import SelfReflectionChecklist from "./prism/SelfReflectionChecklist";
import AnhedonicDepressionSection from "./prism/AnhedonicDepressionSection";
import WhoWeSeeSection from "./prism/WhoWeSeeSection";
import ProvidersSection from "./prism/ProvidersSection";
import ClosingCtaSection from "./prism/ClosingCtaSection";
import {
  PRISM_IMAGES,
  PRISM_PHONE,
  PRISM_PHONE_HREF,
  PRISM_ADDRESS,
  PRISM_HOURS,
  PRISM_BOOKING_URL,
  mechanismSteps,
  sessionSteps,
  scheduleRows,
  researchStats,
  faqItems,
} from "./prism/prismData";

const Prism = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Prism for PTSD & Anhedonic Depression | Neuro Wellness TMS"
        description="Prism is a non-invasive, drug-free EEG neurofeedback treatment, FDA-cleared as an add-on for PTSD, with a protocol for anhedonic depression. Coral Springs, FL."
        canonical="/prism"
        keywords="Prism PTSD treatment Coral Springs, EEG neurofeedback PTSD, GrayMatters Health Prism, non-invasive PTSD therapy South Florida, drug-free PTSD treatment, anhedonic depression, neurofeedback depression anxiety"
        schemaJson={[
          {
            "@context": "https://schema.org",
            "@type": "MedicalTherapy",
            name: "Prism for PTSD",
            description:
              "Prescription EEG neurofeedback device cleared by the FDA as an adjunctive treatment for symptoms associated with PTSD.",
            url: "/prism",
            procedureType: "EEG Neurofeedback",
            relevantSpecialty: "Psychiatry",
            provider: {
              "@id": "https://neurowellnesstms.com/#organization",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.a,
              },
            })),
          },
        ]}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* ── Hero ── */}
        <section className="relative overflow-hidden bg-gradient-navy py-20 text-primary-foreground sm:py-28 lg:py-32">
          <div className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
          <div className="pointer-events-none absolute -right-24 bottom-0 h-64 w-64 rounded-full bg-medium-blue/20 blur-3xl" />
          <div className="container-px relative mx-auto max-w-4xl text-center">
            <div className="mb-6 inline-flex items-center gap-2.5 rounded-full border border-white/20 bg-white/10 px-5 py-2 backdrop-blur-md">
              <ShieldCheck className="h-4 w-4 text-accent" />
              <span className="text-sm font-medium text-white/90">
                Neuro Wellness · Authorized Prism Provider
              </span>
            </div>
            <h1 className="font-headings text-3xl font-bold leading-[1.15] sm:text-4xl md:text-5xl lg:text-5xl xl:text-[3.5rem]">
              A different way to work on your PTSD symptoms
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-primary-foreground/80 sm:text-xl">
              No medication. No retelling your trauma. Just you, learning to
              steady your own brain — with our team beside you.
            </p>
            <p className="mx-auto mt-4 max-w-2xl text-base text-primary-foreground/70">
              Neuro Wellness TMS Centers of America is an authorized provider of
              Prism™, a prescription neurofeedback device cleared by the FDA as
              an adjunctive treatment for symptoms associated with PTSD.
            </p>
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Button
                asChild
                size="lg"
                className="w-full sm:w-auto bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                <a href={PRISM_BOOKING_URL}>
                  Book a consultation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-2 border-white/50 bg-transparent text-white hover:bg-white/15 hover:border-white/70"
              >
                <a href={PRISM_PHONE_HREF}>
                  <Phone className="mr-2 h-4 w-4" />
                  {PRISM_PHONE}
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* ── Section A: Do these feel familiar? ── */}
        <SymptomsSection />

        {/* ── Section C: Self-reflection checklist ── */}
        <SelfReflectionChecklist />

        {/* ── If trauma is still shaping your days ── */}
        <section className="relative bg-light-blue/40 py-16 lg:py-24">
          <div className="container-px mx-auto max-w-3xl">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent">
              If trauma is still shaping your days
            </span>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
              <p>
                Maybe you're sleeping badly, or not at all. Maybe you're on edge
                in ordinary places — a parking lot, a crowded room, a certain
                time of day. Maybe you've done the work already: therapy,
                medication, both, and you're still carrying more than you should
                have to.
              </p>
              <p>
                You're not failing at recovery. PTSD changes how the brain
                handles threat, and that change doesn't respond to willpower. It
                responds to training.
              </p>
              <p>
                If you're a survivor of assault, an accident, a serious illness
                or a natural disaster — or a Veteran carrying what you carried
                home — Prism may be worth discussing with our clinical team.
              </p>
            </div>
          </div>
        </section>

        {/* ── What is Prism? ── */}
        <section className="relative bg-gradient-hero py-16 lg:py-24">
          <div className="container-px mx-auto max-w-3xl">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent">
              What is Prism?
            </span>
            <h2 className="mt-3 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-[2.5rem]">
              A prescription neurofeedback device for PTSD
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
              <p>
                Prism is a prescription medical device that uses EEG
                neurofeedback to help you learn to regulate brain activity
                associated with your PTSD symptoms.
              </p>
              <p>
                It works alongside your existing care — it is not a replacement
                for therapy or medication. Prism is prescribed by a licensed
                healthcare professional and used together with your other
                treatment, which is exactly how it was studied and how it's
                cleared for use.
              </p>
              <p className="font-semibold text-primary">
                Three things make it unusual:
              </p>
            </div>

            {/* Infographic 1: 3 Things That Make PRISM Different */}
            <div className="my-8 overflow-hidden rounded-2xl border border-border shadow-soft bg-card">
              <img
                src={PRISM_IMAGES.threeDifferences}
                alt="3 Things That Make PRISM Different: No trauma retelling required, Non-invasive, Build self-regulation skills"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>

            <div className="mt-8 space-y-6">
              {[
                {
                  title: "You don't talk about the trauma.",
                  body: "Prism doesn't ask you to describe, revisit, or re-experience what happened. Nothing about the sessions is trauma-based.",
                },
                {
                  title: "Nothing enters your body.",
                  body: "No injections, no radiation, no additional medication. A soft cap with sensors, resting on your scalp.",
                },
                {
                  title: "You do the work.",
                  body: "Prism doesn't stimulate your brain or do something to you. It shows you what your brain is doing, in real time, so you can learn to change it. The skill is yours to keep.",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="rounded-2xl border border-border bg-card p-6 shadow-soft transition-all duration-300 hover:border-accent/40 hover:shadow-premium"
                >
                  <h3 className="font-headings text-lg font-semibold text-primary">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
                    {item.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Section B: Not sad, just flat (anhedonic depression) ── */}
        <AnhedonicDepressionSection />

        {/* ── How Prism works (numbered, with EEG trace) ── */}
        <section className="relative overflow-hidden bg-gradient-navy py-16 text-primary-foreground lg:py-24">
          <div className="pointer-events-none absolute -right-20 top-0 h-64 w-64 rounded-full bg-accent/10 blur-3xl" />
          <div className="container-px relative mx-auto max-w-3xl">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent">
              The mechanism
            </span>
            <h2 className="mt-3 font-headings text-2xl font-bold sm:text-3xl md:text-4xl lg:text-[2.5rem]">
              How Prism works
            </h2>

            {/* EEG signature trace */}
            <div className="mt-10">
              <EegTrace />
              <div className="mt-3 flex justify-between text-xs font-medium uppercase tracking-widest text-primary-foreground/50">
                <span>Before training</span>
                <span>After training</span>
              </div>
            </div>

            {/* Infographic 2: How the PRISM Simulation Changes */}
            <div className="my-10 overflow-hidden rounded-2xl border border-primary-foreground/20 shadow-premium bg-card/10 backdrop-blur-sm">
              <img
                src={PRISM_IMAGES.simulationChanges}
                alt="How the PRISM Simulation Changes: More activated to more settled using mental strategies like a memory, a feeling, or a peaceful experience"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>

            <div className="mt-12 space-y-8">
              {mechanismSteps.map((step) => (
                <div
                  key={step.n}
                  className="rounded-2xl border border-primary-foreground/10 bg-primary-foreground/[0.04] p-6 transition-colors hover:border-accent/30 hover:bg-primary-foreground/[0.07]"
                >
                  <div className="flex items-center gap-4">
                    <span className="font-headings text-4xl font-extrabold text-gradient-accent">
                      {step.n}
                    </span>
                    <h3 className="font-headings text-lg font-semibold">
                      {step.title}
                    </h3>
                  </div>
                  <p className="mt-3 text-[15px] leading-relaxed text-primary-foreground/70">
                    {step.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── What a session is actually like (numbered) ── */}
        <section className="relative bg-gradient-hero py-16 lg:py-24">
          <div className="container-px mx-auto max-w-3xl">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent">
              What a session is actually like
            </span>
            <h2 className="mt-3 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-[2.5rem]">
              You'll be in a quiet room with our team
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
              You'll be in a quiet room at our Coral Springs office with a
              table, a chair and a monitor. Your Prism operator stays with you
              throughout.
            </p>

            {/* Infographic 3: What a PRISM Session Looks Like */}
            <div className="my-8 overflow-hidden rounded-2xl border border-border shadow-soft bg-card">
              <img
                src={PRISM_IMAGES.sessionStepByStep}
                alt="What a PRISM Session Looks Like: 1 EEG cap setup, 2 Calibration, 3 Guided training, 4 Done in about 45 minutes"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>

            <div className="mt-10 space-y-4 sm:space-y-5">
              {sessionSteps.map((step) => (
                <div
                  key={step.n}
                  className="rounded-2xl border border-border bg-card p-6 shadow-soft transition-all duration-300 hover:border-accent/40 hover:shadow-premium sm:p-7"
                >
                  <div className="flex items-center gap-4">
                    <span className="font-headings text-4xl font-extrabold text-gradient-accent">
                      {step.n}
                    </span>
                    <h3 className="font-headings text-lg font-semibold text-primary">
                      {step.title}
                    </h3>
                  </div>
                  <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
                    {step.body}
                  </p>
                </div>
              ))}
            </div>

            <p className="mt-8 text-base leading-relaxed text-muted-foreground sm:text-lg">
              What we'll ask of you during cycles: sit upright with feet flat
              and hands resting, keep your eyes open, minimize movement of your
              head and face, breathe normally rather than deeply, and stay
              quiet. You can stretch and talk during breaks. If anything is
              uncomfortable, tell your operator — your only job during a cycle
              is your mental strategy.
            </p>
          </div>
        </section>

        {/* ── Treatment schedule (table & timeline visual) ── */}
        <section className="relative bg-light-blue/40 py-16 lg:py-24">
          <div className="container-px mx-auto max-w-3xl">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent">
              Your treatment schedule
            </span>
            <h2 className="mt-3 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-[2.5rem]">
              A structured course, tailored to you
            </h2>

            {/* Infographic 4: 15 Sessions Over About 8 Weeks */}
            <div className="my-8 overflow-hidden rounded-2xl border border-border shadow-soft bg-card">
              <img
                src={PRISM_IMAGES.sessionsTimeline}
                alt="15 Sessions Over About 8 Weeks: A clear path to a brighter tomorrow with Prism"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>

            <div className="mt-8 overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
              <table className="w-full">
                <tbody>
                  {scheduleRows.map(([label, value]) => (
                    <tr
                      key={label}
                      className="border-b border-border last:border-0"
                    >
                      <th className="w-2/5 px-6 py-4 text-left align-top text-xs font-semibold uppercase tracking-widest text-medium-blue">
                        {label}
                      </th>
                      <td className="px-6 py-4 text-base font-medium text-primary">
                        {value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground sm:text-lg">
              Your Neuro Wellness provider sets the schedule with you and may
              adjust it based on your circumstances and response.
            </p>
          </div>
        </section>

        {/* ── What the research shows ── */}
        <section className="relative bg-gradient-hero py-16 lg:py-24">
          <div className="container-px mx-auto max-w-3xl">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent">
              What the research shows
            </span>
            <h2 className="mt-3 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-[2.5rem]">
              Clinical study results
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
              In a prospective, multi-center clinical study, patients were
              assessed three months after finishing the 15-session course — five
              months from their starting point.
            </p>

            {/* Infographic 5: Clinical Results From a Multi-Center Study */}
            <div className="my-8 overflow-hidden rounded-2xl border border-border shadow-soft bg-card">
              <img
                src={PRISM_IMAGES.clinicalResults}
                alt="Clinical Results From a Multi-Center Study: 67% clinically significant improvement, 32% remission, 90% patient compliance"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>

            <div className="mt-8 space-y-4">
              {researchStats.map((stat) => (
                <div
                  key={stat.value}
                  className="flex items-baseline gap-5 rounded-2xl border border-border bg-card p-5 shadow-soft sm:p-6"
                >
                  <span
                    className="font-headings text-4xl font-extrabold text-gradient-accent sm:text-5xl"
                    style={{
                      lineHeight: 1,
                      minWidth: "3ch",
                    }}
                  >
                    {stat.value}
                  </span>
                  <span className="text-base text-foreground sm:text-lg">
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>

            <p className="mt-8 text-sm italic text-muted-foreground">
              Fruchter E, et al. Amygdala-derived-EEG-fMRI-pattern neurofeedback
              for the treatment of chronic post-traumatic stress disorder: a
              prospective, multicenter, multinational study evaluating clinical
              efficacy. <em>Psychiatry Research.</em> 2024;333:115711.
            </p>

            <p className="mt-6 text-base leading-relaxed text-foreground sm:text-lg">
              That 90% completion figure is worth pausing on. Dropout is one of
              the hardest problems in PTSD care, and a treatment only helps if
              people can stay with it.
            </p>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
              Individual results vary. These are study findings, not a
              prediction for you. Your provider will discuss what's realistic in
              your situation.
            </p>
          </div>
        </section>

        <PrismVideosSection />

        {/* ── Safety and side effects ── */}
        <section className="relative bg-light-blue/40 py-16 lg:py-24">
          <div className="container-px mx-auto max-w-3xl">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent">
              Safety and side effects
            </span>
            <h2 className="mt-3 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-[2.5rem]">
              Mild, well-tolerated, non-invasive
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
              <p>
                In clinical study, the most commonly reported side effects were
                mild — headache, dizziness, and fatigue — and resolved on their
                own after the session ended, without further intervention.
              </p>
              <p>
                Prism is non-invasive. It does not deliver electrical or
                magnetic stimulation to your brain; the EEG cap only reads
                activity, in the way a fitness tracker reads heart rate. There
                are no injections and no radiation exposure.
              </p>
              <p>
                Your Neuro Wellness provider will review your full history to
                determine whether Prism is appropriate for you, and will discuss
                safety information in detail during your consultation.
              </p>
            </div>
          </div>
        </section>

        {/* ── Works with your current treatment ── */}
        <section className="relative bg-gradient-hero py-16 lg:py-24">
          <div className="container-px mx-auto max-w-3xl">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent">
              Adjunctive treatment
            </span>
            <h2 className="mt-3 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-[2.5rem]">
              Prism works with your current treatment, not instead of it
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
              <p>
                Prism is cleared for use as an adjunctive treatment — meaning
                it's designed to be added to your care, under the direction of
                your healthcare professional, alongside other approaches.
              </p>
              <p>
                If you're in therapy, you stay in therapy. Some patients find
                that the regulation skill they build in Prism sessions makes
                other work more accessible. If you're on medication, decisions
                about it remain between you and your prescriber — starting Prism
                doesn't change anything on its own.
              </p>
              <p>
                Whatever you're already doing, bring it to your consultation.
                Prism is one part of a plan, and our team will help you see
                where it fits.
              </p>
            </div>
          </div>
        </section>

        {/* ── Section D: Who we see ── */}
        <WhoWeSeeSection />

        {/* ── FAQ ── */}
        <section className="relative bg-light-blue/40 py-16 lg:py-24">
          <div className="container-px mx-auto max-w-3xl">
            <div className="text-center">
              <span className="text-sm font-semibold uppercase tracking-widest text-accent">
                Common questions
              </span>
              <h2 className="mt-3 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-[2.5rem]">
                Questions, answered.
              </h2>
            </div>
            <Accordion
              type="single"
              collapsible
              className="mt-10 space-y-3 sm:mt-12 sm:space-y-4"
            >
              {faqItems.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="rounded-xl border border-border bg-card px-5 shadow-soft data-[state=open]:border-accent/40 data-[state=open]:shadow-premium"
                >
                  <AccordionTrigger className="text-left font-headings text-base font-semibold text-primary hover:no-underline">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-relaxed text-muted-foreground">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* ── Section E: For referring providers ── */}
        <ProvidersSection />

        {/* ── Section F2: Closing CTA ── */}
        <ClosingCtaSection />

        {/* ── Final CTA ── */}
        <section className="relative overflow-hidden bg-gradient-navy py-20 text-primary-foreground lg:py-28">
          <div className="pointer-events-none absolute -right-24 top-0 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
          <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-medium-blue/20 blur-3xl" />
          <div className="container-px relative mx-auto max-w-3xl text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent">
              Start with a conversation
            </span>
            <h2 className="mt-3 font-headings text-2xl font-bold sm:text-3xl md:text-4xl lg:text-[2.5rem]">
              A clinical consultation, not a commitment
            </h2>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-primary-foreground/80 sm:text-lg">
              We'll review your history and what you've tried, talk through
              whether Prism fits your situation, and answer whatever you want to
              ask. If it isn't the right fit, we'll tell you that and talk about
              what might be.
            </p>
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Button
                asChild
                size="lg"
                className="w-full sm:w-auto bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                <a href={PRISM_BOOKING_URL}>
                  Book a consultation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-2 border-white/50 bg-transparent text-white hover:bg-white/15 hover:border-white/70"
              >
                <a href={PRISM_PHONE_HREF}>
                  <Phone className="mr-2 h-4 w-4" />
                  {PRISM_PHONE}
                </a>
              </Button>
            </div>
            <div className="mt-8 flex flex-col items-center gap-2 text-sm text-primary-foreground/70 sm:flex-row sm:justify-center sm:gap-6">
              <span className="flex items-center gap-1.5">
                <MapPin className="h-4 w-4" /> {PRISM_ADDRESS}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="h-4 w-4" /> {PRISM_HOURS}
              </span>
            </div>
          </div>
        </section>

        {/* ── Compliance footer (verbatim) ── */}
        <footer className="bg-primary-deep px-5 py-12 text-primary-foreground/70 sm:px-8 sm:py-16">
          <div className="container-px mx-auto max-w-3xl text-sm leading-relaxed">
            <p className="mb-4 font-semibold text-white">
              Rx Only. FDA-cleared.
            </p>
            <p className="mb-4">
              <strong className="text-white">Indication for Use:</strong> Prism
              is a neurofeedback software device intended for relaxation and
              stress reduction through the use of EEG biofeedback. The device is
              indicated as an adjunctive treatment of symptoms associated with
              posttraumatic stress disorder (PTSD), to be used under the
              direction of a healthcare professional, together with other
              pharmacological and/or non-pharmacological interventions.
            </p>
            <p className="mb-4">
              Prism™ is a trademark of GrayMatters Health. Neuro Wellness is an
              authorized Prism provider.
            </p>
            <p className="mb-4">
              Individual results may vary. The information on this page is not a
              substitute for professional medical advice. Always discuss
              treatment options and expected outcomes with your physician or
              qualified mental health provider.
            </p>
            <p className="font-semibold text-accent">
              If you are in crisis, call or text 988 (Suicide &amp; Crisis
              Lifeline) or go to your nearest emergency room.
            </p>
          </div>
        </footer>
      </main>

      <RelatedPages
        heading="Explore Our Treatments & Resources"
        links={[
          ...allTreatmentsLinks.filter((l) => l.href !== "/prism"),
          {
            title: "TMS for PTSD",
            href: "/services/ptsd",
            description: "TMS therapy for PTSD symptoms",
          },
          {
            title: "Ketamine for PTSD",
            href: "/ptsd-treatment-coral-springs-fl",
            description: "IV ketamine for trauma",
          },
          ...allDecisionLinks,
        ]}
      />

      {/* Spacer for sticky mobile CTA so footer isn't hidden */}
      <div className="h-20 sm:hidden" />

      <Footer />

      {/* ── Sticky mobile CTA ── */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background/95 backdrop-blur-md px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:hidden">
        <Button
          asChild
          size="lg"
          className="w-full bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
        >
          <a href={PRISM_BOOKING_URL}>
            Book a consultation
            <ArrowRight className="ml-2 h-4 w-4" />
          </a>
        </Button>
      </div>
    </div>
  );
};

export default Prism;
